import { Fragment, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import { ContextMenu, Item, MenuButton } from 'basic-components';
import { EmptySet, FatArrowDown } from 'basic-components/icons';

import { dataSharingQueries } from 'queries/modules/datasharing';

import { Else, If, Then } from 'react-if';

import './ProviderSelector.scss';

const EMPTY_FUN = () => {}

const ProviderSelector = ({ onProviderChange = EMPTY_FUN, shouldUseSearchParams = true, externalProvider = "" }) => {
  const [searchParams] = useSearchParams(),
        navigate = useNavigate(),
        providers = useSelector(state => state.globalState.providers),
        provider = useMemo(() => shouldUseSearchParams ? (searchParams.get("provider") || "") : externalProvider, [externalProvider, searchParams, shouldUseSearchParams]),
        activeProvider = useMemo(() => {
          if(!provider) {
            return providers.find(provider => provider.producerId === 0) ?? dataSharingQueries.LOCAL_PROVIDER
          }
          return providers.find(p => p.name === provider) || {}
        }, [provider, providers]),
        onChange = useCallback(({ value = "" } = {}) => {
          if(shouldUseSearchParams) {
            const currentHash = location.hash
            searchParams.set("provider", value)
            onProviderChange(searchParams)
            navigate(`${window.location.pathname}?${searchParams.toString()}${currentHash}`);
          } else {
            onProviderChange(searchParams, { value })
          }
        }, [navigate, onProviderChange, searchParams, shouldUseSearchParams])

  if(providers.length === 0) {
    return <Fragment/>
  }

  return (
    <ContextMenu
        className="provider-selector__menu"
        gap={4}
        menuButton={(
          <MenuButton key="MenuButton">
            <div className="provider-selector">
              <div className="provider-selector__icon">
                <If condition={activeProvider?.iconUrl}>
                  <Then>
                    {() => (
                      <img
                          src={activeProvider.iconUrl}
                          width="24px"
                      />
                    )}
                  </Then>
                  <Else>
                    {() => (
                      <EmptySet
                          color="grey-dark"
                          height={12}
                          width={12}
                      />
                    )}
                  </Else>
                </If>
              </div>
              <div className="provider-selector__label">
                {activeProvider.readableName}
              </div>
              <div className="provider-selector__border"/>
              <div className="provider-selector__arrow">
                <FatArrowDown
                    color="blue-bright"
                    height={12}
                    width={12}
                />
              </div>
            </div>
          </MenuButton>
        )}
      >
        {providers.map(provider => (
          <Item
              key={`${provider.producerId}`}
              onClick={onChange}
              value={provider.producerId === 0 ? undefined : provider.name}
          >
            <div className="provider-selector__menu__item__icon">
              <img
                  src={provider.iconUrl}
                  width="12px"
              />
            </div>
            <div className="provider-selector__menu__item__full-name">
              {provider.readableName}
            </div>
          </Item>
        ))}
      </ContextMenu>
  )
}

ProviderSelector.propTypes = {
  externalProvider: PropTypes.string,
  onProviderChange: PropTypes.func,
  shouldUseSearchParams: PropTypes.bool
}

export default ProviderSelector;